(() => {
  if (window.VenadaCollection) return;

  const controllers = new WeakMap();
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function isVisible(element, view = window) {
    if (!(element instanceof HTMLElement)) return false;
    if (element.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
    const style = view.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden';
  }

  function focusableElements(container, view = window) {
    if (!container) return [];
    return [...container.querySelectorAll(focusableSelector)].filter((element) => isVisible(element, view));
  }

  function createController(root, dependencies = {}) {
    const doc = dependencies.document ?? document;
    const view = dependencies.window ?? window;
    const setTimer = dependencies.setTimer ?? view.setTimeout.bind(view);
    const clearTimer = dependencies.clearTimer ?? view.clearTimeout.bind(view);
    const abortController = new AbortController();
    const listenerOptions = {signal: abortController.signal};
    const opener = root.querySelector('[data-venada-filter-open]');
    const sheet = root.querySelector('[data-venada-filter-sheet]');
    const dialog = root.querySelector('[data-venada-filter-dialog]');
    const closeButton = root.querySelector('[data-venada-filter-close]');
    const backdrop = root.querySelector('[data-venada-filter-backdrop]');
    const mobileMedia = dependencies.mobileMedia ?? view.matchMedia?.('(max-width: 900px)') ?? null;
    let previousBodyOverflow = null;
    let focusTimer = null;

    function restoreBody() {
      if (previousBodyOverflow === null) return;
      doc.body.style.overflow = previousBodyOverflow;
      previousBodyOverflow = null;
    }

    function close({returnFocus = true, blurHiddenFocus = false} = {}) {
      if (!sheet || sheet.hidden) {
        opener?.setAttribute('aria-expanded', 'false');
        restoreBody();
        return;
      }
      if (focusTimer !== null) clearTimer(focusTimer);
      sheet.hidden = true;
      opener?.setAttribute('aria-expanded', 'false');
      if (blurHiddenFocus && dialog?.contains(doc.activeElement)) {
        doc.activeElement?.blur?.();
      }
      restoreBody();
      if (returnFocus && opener?.isConnected !== false) {
        focusTimer = setTimer(() => opener.focus(), 0);
      }
    }

    function open() {
      if (!sheet || !dialog || !opener) return;
      if (mobileMedia && mobileMedia.matches === false) return;
      if (previousBodyOverflow === null) previousBodyOverflow = doc.body.style.overflow;
      sheet.hidden = false;
      opener.setAttribute('aria-expanded', 'true');
      doc.body.style.overflow = 'hidden';
      if (focusTimer !== null) clearTimer(focusTimer);
      focusTimer = setTimer(() => (closeButton ?? focusableElements(dialog, view)[0])?.focus(), 0);
    }

    function trapFocus(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      const elements = focusableElements(dialog, view);
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (!first || !last) {
        event.preventDefault();
        dialog?.focus();
        return;
      }
      if (event.shiftKey && doc.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && doc.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    function submitChangedForm(event) {
      const control = event.target.closest?.('[data-venada-submit-on-change]');
      if (!control || !root.contains(control)) return;
      control.form?.requestSubmit();
    }

    function handleViewportChange(event) {
      if (event.matches === false) close({returnFocus: false, blurHiddenFocus: true});
    }

    opener?.addEventListener('click', open, listenerOptions);
    closeButton?.addEventListener('click', () => close(), listenerOptions);
    backdrop?.addEventListener('click', () => close(), listenerOptions);
    dialog?.addEventListener('keydown', trapFocus, listenerOptions);
    root.addEventListener('change', submitChangedForm, listenerOptions);
    mobileMedia?.addEventListener?.('change', handleViewportChange);

    return {
      open,
      close,
      destroy() {
        abortController.abort();
        mobileMedia?.removeEventListener?.('change', handleViewportChange);
        close({returnFocus: false, blurHiddenFocus: true});
        if (focusTimer !== null) clearTimer(focusTimer);
      }
    };
  }

  function init(root) {
    if (!root || controllers.has(root)) return controllers.get(root);
    const controller = createController(root);
    controllers.set(root, controller);
    return controller;
  }

  function initAll(scope = document) {
    if (scope.matches?.('[data-venada-collection]')) init(scope);
    scope.querySelectorAll?.('[data-venada-collection]').forEach(init);
  }

  function destroyAll(scope) {
    const roots = [];
    if (scope?.matches?.('[data-venada-collection]')) roots.push(scope);
    scope?.querySelectorAll?.('[data-venada-collection]').forEach((root) => roots.push(root));
    roots.forEach((root) => {
      controllers.get(root)?.destroy();
      controllers.delete(root);
    });
  }

  window.VenadaCollection = {createController, focusableElements, initAll};

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), {once: true});
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));
})();
